import deepEqual from "deep-equal";
import { useEffect, type PropsWithChildren } from "react";

import type { Session } from "@supabase/supabase-js";

import apiClient from "@/api/apiClient";
import queryClient, { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import { useAuthSession } from "@/api/resources/auth/hooks";
import { authQueryKeys } from "@/api/resources/auth/queries";

const AuthProvider = ({ children }: PropsWithChildren) => {
  const { data: session } = useAuthSession();

  useEffect(() => {
    const isSessionUnchanged = (session?: Session | null, newSession?: Session | null) => {
      if (!session || !newSession) return false;
      return deepEqual(session, newSession, { strict: true });
    };
    const { data } = apiClient.auth.onAuthStateChange((event, newSession) => {
      // TODO: this event might not be needed: https://supabase.com/docs/reference/javascript/auth-onauthstatechange
      // TODO: it fires just about everytime the user changes tabs, kinda duplicates "SIGNED_IN"
      // if (event === "INITIAL_SESSION") {
      //   setSession(session);
      //   if (session) void getUserAndProfileAndPermissions();
      // }
      if (event === "SIGNED_IN") {
        // re-evaluate ALL app data, if the session has changed on signin/login (pretty much every time this events fires)
        if (!isSessionUnchanged(session, newSession)) {
          void queryClient.invalidateQueries({ queryKey: MASTER_INVALIDATION_KEY });
        }
      } else if (event === "USER_UPDATED") {
        // re-evaluate all user details if user is updated (session, user, profile, permissions)
        if (!isSessionUnchanged(session, newSession)) {
          void queryClient.invalidateQueries({ queryKey: [...authQueryKeys.all()] });
        }
      } else if (event === "TOKEN_REFRESHED") {
        // re-evaluate all user details if user is updated (session, user, profile, permissions)
        if (!isSessionUnchanged(session, newSession)) {
          void queryClient.invalidateQueries({ queryKey: [...authQueryKeys.all()] });
        }
      } else if (event === "SIGNED_OUT") {
        // doing nothing here
        // see `useSignOut` in src/api/resources/auth/hooks.ts for all details, what is happening on signout/logout
      }
    });
    return () => data.subscription.unsubscribe();
  }, [session]);

  return children;
};

export default AuthProvider;
